import {
  Hct,
  SchemeContent,
  SchemeExpressive,
  SchemeFidelity,
  SchemeFruitSalad,
  SchemeMonochrome,
  SchemeNeutral,
  SchemeRainbow,
  SchemeTonalSpot,
  SchemeVibrant,
  TonalPalette,
  argbFromHex,
  hexFromArgb,
} from "@material/material-color-utilities";

import { SelectedTheme, TypeTheme } from "@revolt/state/stores/Theme";

/**
 * Generate the Material variables from the given properties
 *
 * Currently only generates color keys
 */
export function createMaterialColourVariables<P extends string>(
  theme: SelectedTheme,
  prefix: P,
): addPrefixToObject<MaterialColours, P> {
  switch (theme.preset) {
    case "you":
      return Object.entries(
        generateMaterialYouScheme(
          theme.accent,
          theme.darkMode,
          theme.contrast,
          theme.variant,
          theme.surface,
        ),
      ).reduce(
        (d, [key, value]) => ({
          ...d,
          [`${prefix}${key}`]: value,
        }),
        {} as addPrefixToObject<MaterialColours, P>,
      );
    default:
      return {} as never;
  }
}

/**
 * Create R,G,B triplets for MDUI variables
 */
export function createMduiColourTriplets<P extends string>(
  theme: SelectedTheme,
  prefix: P,
): addPrefixToObject<MaterialColours, P> {
  const variables = createMaterialColourVariables(theme, prefix);

  for (const key in variables) {
    const [_, r, g, b] = /#([0-9A-F]{2})([0-9A-F]{2})([0-9A-F]{2})/i.exec(
      variables[key as keyof typeof variables] as string,
    )!;

    variables[key as keyof typeof variables] =
      `${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(b, 16)}` as never;
  }

  return variables;
}

type addPrefixToObject<T, P extends string> = {
  [K in keyof T as K extends string ? `${P}${K}` : never]: T[K];
};

type _addSuffixToObject<T, S extends string> = {
  [K in keyof T as K extends string ? `${K}${S}` : never]: T[K];
};

type MaterialColours = {
  primary: string;
  "on-primary": string;
  "primary-container": string;
  "on-primary-container": string;
  secondary: string;
  "on-secondary": string;
  "secondary-container": string;
  "on-secondary-container": string;
  tertiary: string;
  "on-tertiary": string;
  "tertiary-container": string;
  "on-tertiary-container": string;
  error: string;
  "on-error": string;
  "error-container": string;
  "on-error-container": string;

  "primary-fixed": string;
  "primary-fixed-dim": string;
  "on-primary-fixed": string;
  "on-primary-fixed-variant": string;
  "secondary-fixed": string;
  "secondary-fixed-dim": string;
  "on-secondary-fixed": string;
  "on-secondary-fixed-variant": string;
  "tertiary-fixed": string;
  "tertiary-fixed-dim": string;
  "on-tertiary-fixed": string;
  "on-tertiary-fixed-variant": string;

  "surface-dim": string;
  surface: string;
  "surface-bright": string;

  "surface-container-lowest": string;
  "surface-container-low": string;
  "surface-container": string;
  "surface-container-high": string;
  "surface-container-highest": string;

  "on-surface": string;
  "on-surface-variant": string;
  outline: string;
  "outline-variant": string;

  "inverse-surface": string;
  "inverse-on-surface": string;
  "inverse-primary": string;

  scrim: string;
  shadow: string;
};

/**
 * How rich the main accent (gold) colour is; the stock tonal palette uses about 36
 */
const ACCENT_CHROMA = 44;

/**
 * Dark surface palettes (Majalis): replace the grey surfaces in dark mode
 */
const SURFACE_PALETTES: Record<
  "black" | "navy" | "midnight",
  Partial<MaterialColours>
> = {
  black: {
    "surface-dim": "#000000",
    surface: "#000000",
    "surface-bright": "#1E1E20",
    "surface-container-lowest": "#000000",
    "surface-container-low": "#0B0B0C",
    "surface-container": "#111112",
    "surface-container-high": "#18181A",
    "surface-container-highest": "#202023",
    "on-surface": "#EDEDED",
    "on-surface-variant": "#A3A3A8",
    outline: "#5C5C62",
    "outline-variant": "#26262A",
    "primary-container": "#2A2A2E",
    "on-primary-container": "#EDEDED",
    "secondary-container": "#2A2A2E",
    "on-secondary-container": "#EDEDED",
  },
  navy: {
    "surface-dim": "#000000",
    surface: "#0B1020",
    "surface-bright": "#172036",
    "surface-container-lowest": "#000000",
    "surface-container-low": "#070B16",
    "surface-container": "#0B1020",
    "surface-container-high": "#121A30",
    "surface-container-highest": "#172036",
    "on-surface": "#E8ECF8",
    "on-surface-variant": "#9AA5C0",
    outline: "#4A5578",
    "outline-variant": "#1C2842",
    "primary-container": "#1F2B4A",
    "on-primary-container": "#E8ECF8",
    "secondary-container": "#1F2B4A",
    "on-secondary-container": "#E8ECF8",
  },
  midnight: {
    "surface-dim": "#050914",
    surface: "#0E1830",
    "surface-bright": "#1B2A4A",
    "surface-container-lowest": "#050914",
    "surface-container-low": "#0A1226",
    "surface-container": "#0E1830",
    "surface-container-high": "#15223F",
    "surface-container-highest": "#1B2A4A",
    "on-surface": "#E8EEFA",
    "on-surface-variant": "#9DADCC",
    outline: "#50628A",
    "outline-variant": "#22345A",
    "primary-container": "#243759",
    "on-primary-container": "#E8EEFA",
    "secondary-container": "#243759",
    "on-secondary-container": "#E8EEFA",
  },
};

/**
 * Generate a Material You colour scheme
 * @param accent Accent colour in hex format
 * @param darkMode Dark mode
 * @param constrat Constrast level
 * @returns Material colours
 */
function generateMaterialYouScheme(
  accent: string,
  darkMode: boolean,
  contrastLevel: number,
  variant: TypeTheme["m3Variant"],
  surface: TypeTheme["m3Surface"],
): MaterialColours {
  const hct = Hct.fromInt(argbFromHex(accent));

  // Majalis: shift the scale so "Normal" has the punch "More contrast" used to have
  const contrast = 0.5 + contrastLevel / 2;

  let scheme;
  switch (variant) {
    case "content":
      scheme = new SchemeContent(hct, darkMode, contrast);
      break;
    case "expressive":
      scheme = new SchemeExpressive(hct, darkMode, contrast);
      break;
    case "fidelity":
      scheme = new SchemeFidelity(hct, darkMode, contrast);
      break;
    case "fruit_salad":
      scheme = new SchemeFruitSalad(hct, darkMode, contrast);
      break;
    case "monochrome":
      scheme = new SchemeMonochrome(hct, darkMode, contrast);
      break;
    case "neutral":
      scheme = new SchemeNeutral(hct, darkMode, contrast);
      break;
    case "rainbow":
      scheme = new SchemeRainbow(hct, darkMode, contrast);
      break;
    case "vibrant":
      scheme = new SchemeVibrant(hct, darkMode, contrast);
      break;
    case "tonal_spot":
    default:
      scheme = new SchemeTonalSpot(hct, darkMode, contrast);
      break;
  }

  // Surfaces and text come from a pure grey palette so only accents carry colour
  const grey = new SchemeMonochrome(hct, darkMode, contrast);

  // Majalis: teal as the supporting accent (tertiary roles); gold stays primary
  const teal = new SchemeTonalSpot(
    Hct.fromInt(argbFromHex("#2A9D8F")),
    darkMode,
    contrast,
  );

  // Majalis: a richer accent than the stock tonal palette allows
  const richPalette = TonalPalette.fromHueAndChroma(
    hct.hue,
    Math.max(scheme.primaryPalette.chroma, ACCENT_CHROMA + 16 * contrastLevel, 28),
  );
  const richer = (argb: number) => richPalette.tone(Hct.fromInt(argb).tone);

  const colours: MaterialColours = {
    primary: hexFromArgb(richer(scheme.primary)),
    "on-primary": hexFromArgb(scheme.onPrimary),
    "primary-container": hexFromArgb(grey.secondaryContainer),
    "on-primary-container": hexFromArgb(grey.onSecondaryContainer),
    secondary: hexFromArgb(grey.secondary),
    "on-secondary": hexFromArgb(grey.onSecondary),
    "secondary-container": hexFromArgb(grey.secondaryContainer),
    "on-secondary-container": hexFromArgb(grey.onSecondaryContainer),
    tertiary: hexFromArgb(teal.primary),
    "on-tertiary": hexFromArgb(teal.onPrimary),
    "tertiary-container": hexFromArgb(teal.primaryContainer),
    "on-tertiary-container": hexFromArgb(teal.onPrimaryContainer),
    error: hexFromArgb(scheme.error),
    "on-error": hexFromArgb(scheme.onError),
    "error-container": hexFromArgb(scheme.errorContainer),
    "on-error-container": hexFromArgb(scheme.onErrorContainer),

    "primary-fixed": hexFromArgb(richer(scheme.primaryFixed)),
    "primary-fixed-dim": hexFromArgb(richer(scheme.primaryFixedDim)),
    "on-primary-fixed": hexFromArgb(scheme.onPrimaryFixed),
    "on-primary-fixed-variant": hexFromArgb(scheme.onPrimaryFixedVariant),
    "secondary-fixed": hexFromArgb(scheme.secondaryFixed),
    "secondary-fixed-dim": hexFromArgb(scheme.onSecondaryFixed),
    "on-secondary-fixed": hexFromArgb(scheme.onSecondaryFixed),
    "on-secondary-fixed-variant": hexFromArgb(scheme.onSecondaryFixedVariant),
    "tertiary-fixed": hexFromArgb(teal.primaryFixed),
    "tertiary-fixed-dim": hexFromArgb(teal.primaryFixedDim),
    "on-tertiary-fixed": hexFromArgb(teal.onPrimaryFixed),
    "on-tertiary-fixed-variant": hexFromArgb(teal.onPrimaryFixedVariant),

    "surface-dim": hexFromArgb(grey.surfaceDim),
    surface: hexFromArgb(grey.surface),
    "surface-bright": hexFromArgb(grey.surfaceBright),

    "surface-container-lowest": hexFromArgb(grey.surfaceContainerLowest),
    "surface-container-low": hexFromArgb(grey.surfaceContainerLow),
    "surface-container": hexFromArgb(grey.surfaceContainer),
    "surface-container-high": hexFromArgb(grey.surfaceContainerHigh),
    "surface-container-highest": hexFromArgb(grey.surfaceContainerHighest),

    "on-surface": hexFromArgb(grey.onSurface),
    "on-surface-variant": hexFromArgb(grey.onSurfaceVariant),
    outline: hexFromArgb(grey.outline),
    "outline-variant": hexFromArgb(grey.outlineVariant),

    "inverse-surface": hexFromArgb(grey.inverseSurface),
    "inverse-on-surface": hexFromArgb(grey.inverseOnSurface),
    "inverse-primary": hexFromArgb(scheme.inversePrimary),

    scrim: hexFromArgb(scheme.scrim),
    shadow: hexFromArgb(scheme.shadow),
  };

  const palette =
    darkMode && surface !== "default" ? SURFACE_PALETTES[surface] : undefined;
  return palette ? { ...colours, ...palette } : colours;
}
